import {isNil} from "lodash";
import {Component, Suspense, useContext, useEffect, useMemo} from "react";
import {Placeholder} from "react-bootstrap";
import {getDashboardItemData} from "../../api/dashboard/getDashboardItemData";
import {ParamTypes} from "../../constans/paramTypes";
import {UnitTypes} from "../../constans/unitTypes";
import {useSQLChartFormatters} from "../../hooks/useSQLChartFormatters";
import {getParameterFormatter} from "../../utils/DashboardUtils";
import {getPercent} from "../../utils/GaugeUtils";
import {convertWeightToBaseUnit} from "../../utils/UnitUtils";
import Gauge from "../basics/gauge/Gauge";
import SkeletonGauge from "../skeletons/SkeletonGauge";
import "./_sql-gauge-wrapper.scss";
import SQLNumber from "./SQLNumber";
import fetchData from "../../api/fetch-suspense-wrapper/FetchSuspenseWrapper";
import InfoBox from "../basics/info-box/InfoBox";
import {useTranslation} from "react-i18next";
import DashboardItemContext from "../../context/DashboardItemContext";

function SQLGaugeWrapperRenderer({
    parameter,
    value,
    keysDictionary,
    translatedIDs,
}) {
    const formatter = useMemo(
        () => getParameterFormatter(parameter, keysDictionary),
        [parameter, keysDictionary]
    );

    const {min, max} = useMemo(() => {
        if (formatter !== ParamTypes.WEIGHT)
            return {min: parameter.min, max: parameter.max};
        if (parameter.min === undefined || parameter.max === undefined)
            return {min: 0, max: 0};
        return {
            min: convertWeightToBaseUnit(parameter.min, {
                fromUnit: UnitTypes.MEDIUM,
            }),
            max: convertWeightToBaseUnit(parameter.max, {
                fromUnit: UnitTypes.MEDIUM,
            }),
        };
    }, [parameter, formatter]);

    const getFormatter = useSQLChartFormatters();

    const percent = useMemo(
        () => getPercent(value, min, max),
        [value, min, max]
    );

    const formattedValue = useMemo(() => {
        if (isNil(value)) return value;
        return +value.toFixed(2);
    }, [value]);

    console.log(formattedValue);

    return (
        <Gauge
            label={translatedIDs[parameter.id]}
            value={formattedValue}
            valueFormatter={getFormatter(formatter, {
                precision: parameter.precision,
            })}
            min={min}
            max={max}
            percent={percent}
            showLegend
            reverse={parameter.reverse}
        />
    );
}

function SQLNumberWrapperRenderer({
    parameter,
    value,
    keysDictionary,
    translatedIDs,
}) {
    const formatter = useMemo(
        () => getParameterFormatter(parameter, keysDictionary),
        [parameter, keysDictionary]
    );

    const getFormatter = useSQLChartFormatters();

    return (
        <SQLNumber
            name={translatedIDs[parameter.id]}
            value={value}
            valueFormatter={getFormatter(formatter, {
                precision: parameter.precision,
            })}
        />
    );
}

function SQLGaugeAndNumberWrapperRenderer({
    resource,
    parameters,
    Type,
    maxRendered,
    keysDictionary,
}) {
    const {Data, ResultID} = resource.read();

    const context = useContext(DashboardItemContext);

    console.error(context);

    useEffect(() => {
        if (context?.setResultID) context.setResultID(ResultID);
    }, [ResultID]); // eslint-disable-line react-hooks/exhaustive-deps

    const Component = useMemo(() => {
        if (Type === "Gauge") return SQLGaugeWrapperRenderer;
        return SQLNumberWrapperRenderer;
    }, [Type]);

    const parametersToRender = useMemo(() => {
        return parameters.filter((item) => !!item.show);
    }, [parameters]);

    const translatedIDs = useMemo(() => {
        const tmp = {};
        for (let row of parameters) {
            tmp[row.id] = row.parameterName;
        }
        return tmp;
    }, [parameters]);

    return (
        <div className={`sql-gauge-wrapper ${Type}`}>
            {parametersToRender
                .slice(0, maxRendered)
                .map((parameter, index) => (
                    <Component
                        key={parameter.id}
                        parameter={parameter}
                        value={Data[0]?.[parameter.id]}
                        keysDictionary={keysDictionary}
                        translatedIDs={translatedIDs}
                    />
                ))}
        </div>
    );
}

function SQLGaugeAndNumberSkeleton({amount, type}) {
    if (type === "Gauge") {
        return (
            <div className="sql-gauge-wrapper h-100">
                {[...Array(amount).keys()].map((item) => (
                    <Placeholder
                        key={item}
                        className="w-100 text-center h-100"
                        animation="glow">
                        <SkeletonGauge />
                        <Placeholder className="w-100" />
                    </Placeholder>
                ))}
            </div>
        );
    }
    return (
        <div className="sql-gauge-wrapper h-100">
            {[...Array(amount).keys()].map((item) => (
                <Placeholder
                    key={item}
                    className="w-100 text-center h-100"
                    animation="glow">
                    <Placeholder className="w-100" style={{height: "2rem"}} />
                    <Placeholder className="w-100" style={{height: ".8rem"}} />
                </Placeholder>
            ))}
        </div>
    );
}

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error) {
        return {hasError: true};
    }

    render() {
        if (this.state.hasError) {
            const {t} = this.props;
            return (
                <InfoBox boxColor="error" outlined>
                    <i className="fa-solid fa-exclamation-circle me-2" />
                    <span>{t("newSettings.devices.settingsInfo.noData")}</span>
                </InfoBox>
            );
        }

        return this.props.children;
    }
}

export default function SQLGaugeAndNumberWrapper({
    Parameters,
    filterValue,
    keysDictionary,
    filterItem,
    Type,
    maxRendered,
    dashboardID,
    itemID,
    Options,
    Filter,
    edit,
    globalFilter,
}) {
    const {t, i18n} = useTranslation();

    const resource = useMemo(() => {
        if (Parameters.length === 0 || !dashboardID) return null;
        return fetchData(
            getDashboardItemData({
                dashboardID,
                itemID,
                filterItem: filterItem ?? [],
                filterValue,
                locale: i18n.language,
                globalFilter,
                overrideParameters: edit ? Parameters : undefined,
                overrideOptions: edit ? Options : undefined,
                overrideFilter: edit ? Filter : undefined,
                overrideType: edit ? Type : undefined,
            })
        );
    }, [
        dashboardID,
        itemID,
        filterItem,
        filterValue,
        i18n.language,
        Filter,
        Options,
        Parameters,
        Type,
        edit,
        globalFilter,
    ]);

    if (!resource) {
        return (
            <div className="w-100 h-100" data-wait="true">
                <SQLGaugeAndNumberSkeleton
                    amount={Parameters.length}
                    type={Type}
                />
            </div>
        );
    }
    return (
        <ErrorBoundary t={t}>
            <Suspense
                fallback={
                    <div className="w-100 h-100" data-wait="true">
                        <SQLGaugeAndNumberSkeleton
                            amount={Parameters.length}
                            type={Type}
                        />
                    </div>
                }>
                <SQLGaugeAndNumberWrapperRenderer
                    Type={Type}
                    maxRendered={maxRendered}
                    keysDictionary={keysDictionary}
                    parameters={Parameters}
                    resource={resource}
                />
            </Suspense>
        </ErrorBoundary>
    );
}
